import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';

import {
  NotificationService,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_WARNING,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import * as Actions from './scan-to-verify.actions';
import * as EntitiesActions from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import * as Api from '../../../store/scan-to-print//scan-to-print.api';
import * as ScanToPrintActions from '../../../store/scan-to-print/scan-to-print.actions';
import { ScanToVerifyReader } from '../../../store/scan-to-print/scan-to-verify-reader';

const messages = defineMessages({
  shipmentItemsVerified: {
    description: 'Success Toast',
    defaultMessage: 'Shipment items verified.',
    id: 'VzXQS7',
  },
  shipmentWarningToast: {
    description: 'Warning Toast',
    defaultMessage: 'There was a problem verifying this shipment. Please try again.',
    id: 'rR2xss',
  },
});

export const getShipmentSuccess = (action$, { getState }) => {
  return action$.pipe(
    ofType(ScanToPrintActions.GET_SHIPMENT_SUCCESS),
    mergeMap(({ fulfillmentPlanId }) => {
      const state = getState();
      const reader = new ScanToVerifyReader({ state });
      const isVerified = reader.isFulfillmentPlanVerified({
        fulfillmentPlanId,
      });
      if (isVerified) {
        return of(Actions.setVerificationStatusSuccess({ isVerified }));
      }
      return EMPTY;
    }),
  );
};

export const verifyShipmentRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.SCAN_TO_VERIFY_SUBMIT_VERIFICATION),
    mergeMap(() => {
      const verificationStatus = {
        isVerified: true,
      };
      return of(Actions.setVerificationStatusRequest(verificationStatus));
    }),
  );
};

export const editVerifiedItems = (action$) => {
  return action$.pipe(
    ofType(Actions.EDIT_VERIFIED_ITEMS),
    mergeMap(() => {
      const verificationStatus = {
        isVerified: false,
      };
      return of(Actions.setVerificationStatusRequest(verificationStatus));
    }),
  );
};

export const setVerificationStatus = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SET_VERIFICATION_STATUS_REQUEST),
    mergeMap(({ verificationStatus }) => {
      const state = getState();
      const fulfillmentPlanId = Global.selectScanToPrintFulfillmentPlanId(state);
      const fulfillmentPlan =
        Global.selectEntitiesFulfillmentPlanById(state, {
          fulfillmentPlanId,
        }) || {};
      const { salesOrdersFulfilledIds: salesOrderIds } = fulfillmentPlan;
      const { isVerified } = verificationStatus;
      return Api.verifyShipment(fulfillmentPlanId, verificationStatus).pipe(
        mergeMap(({ data }) => {
          if (data) {
            return of(EntitiesActions.bulkRequestEntities({ salesOrderIds }), Actions.setVerificationStatusSuccess({ isVerified }));
          }
          return of(Actions.setVerificationStatusFailure());
        }),
        catchError((error) => {
          if (error && Array.isArray(error.Errors)) {
            const errors = error.Errors.map((e) => ({
              id: 'scan.verify.error',
              defaultMessage: e.Message,
            }));
            return of(ScanToPrintActions.setErrorMessage(errors));
          }

          return EMPTY;
        }),
      );
    }),
  );
};

export const popUpVerificationToast = (action$) => {
  return action$.pipe(
    ofType(Actions.SET_VERIFICATION_STATUS_SUCCESS),
    mergeMap(({ verificationStatus }) => {
      const { isVerified } = verificationStatus;
      if (isVerified) {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.shipmentItemsVerified} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      }
      return EMPTY;
    }),
  );
};

export const popUpVerificationErrorToast = (action$) => {
  return action$.pipe(
    ofType(Actions.SET_VERIFICATION_STATUS_FAILURE),
    mergeMap(() => {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_WARNING,
        content: <FormattedMessage {...messages.shipmentWarningToast} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
      return EMPTY;
    }),
  );
};
